import { ThumbsUp, Share2, Clock } from 'lucide-react';
import { GeometricAvatar } from '../shared/GeometricAvatar';

const vibeConfig = {
  lockedin: { emoji: '🔥', label: 'LOCKED IN', colors: 'bg-lime-500 text-black border-lime-400' },
  disaster: { emoji: '😂', label: 'DISASTER', colors: 'bg-orange-500 text-black border-orange-400' },
  sentanyway: { emoji: '🤷', label: 'SENT ANYWAY', colors: 'bg-blue-500 text-white border-blue-400' },
  unhinged: { emoji: '💀', label: 'UNHINGED', colors: 'bg-purple-600 text-white border-purple-400' },
  bigW: { emoji: '🏆', label: 'BIG W', colors: 'bg-yellow-500 text-black border-yellow-400' },
  rejected: { emoji: '💔', label: 'REJECTED', colors: 'bg-pink-600 text-white border-pink-400' },
  plot_twist: { emoji: '🤯', label: 'PLOT TWIST', colors: 'bg-cyan-500 text-black border-cyan-400' },
};

const categoryColors = {
  social: 'bg-blue-600',
  money: 'bg-green-600',
  creative: 'bg-purple-600',
  gym: 'bg-red-600',
  'anti-opt': 'bg-orange-600',
  chaos: 'bg-yellow-600',
};

export const FeedPost = ({
  post,
  userId,
  userVotes,
  activeCommentPost,
  setActiveCommentPost,
  comments,
  commentText,
  setCommentText,
  commentCooldown,
  isPostingComment,
  addComment,
  upvotePost,
  flagPost,
  deletePost,
  flagComment,
  deleteComment,
  shareToSocial,
  formatTimestamp,
  setViewingProfile,
  setShowProfile,
  getUserCategoryBreakdown,
  getUserRecentSends,
  AUTO_HIDE_FLAG_THRESHOLD,
  MAX_COMMENT_LENGTH,
  MIN_COMMENT_LENGTH,
}) => {
  const vibe = vibeConfig[post.vibe];
  const hasVoted = userVotes[post.id];
  const isOwnPost = post.userId === userId;
  const stats = post.userStats || { streak: 0, totalSends: 0 };

  return (
    <div
      className={`p-5 sm:p-7 transition-all duration-300 hover:scale-[1.01] ${isOwnPost
        ? 'bg-gradient-to-br from-blue-900 to-indigo-900 border-3 sm:border-4 border-blue-400 hover:shadow-xl hover:shadow-blue-500/30'
        : 'bg-gray-900 border-3 sm:border-4 border-gray-700 hover:border-lime-500 hover:shadow-xl hover:shadow-lime-500/20'
        }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isOwnPost && (
            <span className="px-3 py-1 bg-blue-500 border-2 border-blue-300 text-xs font-black text-white uppercase">
              YOU
            </span>
          )}
          <button
            onClick={() => {
              const profile = {
                username: post.username || 'Anonymous',
                userId: post.userId,
                bio: post.bio,
                memberSince: post.timestamp,
                stats: post.userStats || { streak: 0, totalSends: 0, longestStreak: 0 },
                categoryBreakdown: getUserCategoryBreakdown(post.userId),
                recentSends: getUserRecentSends(post.userId),
              };
              setViewingProfile(profile);
              setShowProfile(true);
            }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <GeometricAvatar username={post.username || 'Anonymous'} size={24} />
            <span className="text-xs font-black text-gray-400 hover:text-white">{post.username || 'Anonymous'}</span>
          </button>
          {stats.streak >= 7 && (
            <span className="text-xs px-2 py-0.5 bg-orange-500 text-black font-black border border-orange-400">
              🔥 {stats.streak} STREAK
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isOwnPost && (
            <button
              onClick={() => flagPost(post.id)}
              disabled={post.flaggedBy && post.flaggedBy.includes(userId)}
              className="text-xs font-black text-gray-500 hover:text-red-400 transition-all disabled:opacity-50 px-2 py-1 border border-gray-700 hover:border-red-500"
            >
              🚩
            </button>
          )}
          {isOwnPost && (
            <button
              onClick={() => deletePost(post.id)}
              className="text-xs font-black text-red-400 hover:text-red-300 transition-all px-2 py-1 border border-red-700 hover:border-red-500 bg-red-900/20"
            >
              DELETE
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`text-xs font-black px-3 py-1 border-2 ${vibe.colors}`}>
          {vibe.emoji} {vibe.label}
        </span>
        <span className={`text-xs font-black px-3 py-1 border-2 ${categoryColors[post.category]} text-white border-white`}>
          {post.category === 'anti-opt' ? 'ANTI-OPT' : post.category.toUpperCase()}
        </span>
        {post.sendAgain && (
          <span className="text-xs font-black px-3 py-1 border-2 bg-black text-lime-500 border-lime-500">
            ✓ WOULD SEND AGAIN
          </span>
        )}
      </div>

      <p className="mb-5 leading-relaxed font-bold text-white text-base sm:text-lg break-words">{post.text}</p>

      <div className="border-t-2 border-gray-800 pt-4 mt-1">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => upvotePost(post.id)}
              className={`flex items-center gap-2 px-4 py-2 font-black uppercase text-sm border-2 transition-all ${hasVoted
                ? 'bg-lime-500 text-black border-lime-400'
                : 'bg-black text-lime-500 border-lime-500 hover:bg-lime-900'
                }`}
            >
              <ThumbsUp size={16} className={hasVoted ? 'fill-current' : ''} />
              {post.votes}
            </button>
            <button
              onClick={() => {
                const newPostId = activeCommentPost === post.id ? null : post.id;
                setActiveCommentPost(newPostId);
                if (newPostId) {
                  setTimeout(() => {
                    document.getElementById(`comment-section-${post.id}`)?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'nearest'
                    });
                  }, 100);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 font-black uppercase text-sm border-2 transition-all ${activeCommentPost === post.id
                ? 'bg-purple-500 text-black border-purple-400'
                : 'bg-black text-purple-500 border-purple-500 hover:bg-purple-900'
                }`}
            >
              💬 {comments[post.id]?.length || 0}
            </button>
            {isOwnPost && (
              <button
                onClick={() => shareToSocial('post', { text: post.text })}
                className="flex items-center gap-2 px-4 py-2 font-black uppercase text-sm border-2 bg-blue-600 text-white border-blue-400 hover:bg-blue-500 transition-all"
              >
                <Share2 size={16} />
                <span className="hidden sm:inline">SHARE</span>
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500 font-bold uppercase flex items-center gap-1 justify-end">
            <Clock size={12} />
            {formatTimestamp(post.timestamp)}
          </span>
        </div>

        {/* Comment Section */}
        {activeCommentPost === post.id && (
          <div id={`comment-section-${post.id}`} className="mt-4 pt-4 border-t-2 border-gray-700">
            {comments[post.id] && comments[post.id].length > 0 ? (
              <div className="space-y-3 mb-4">
                {comments[post.id]
                  .filter(c => (c.flags || 0) < AUTO_HIDE_FLAG_THRESHOLD)
                  .map(comment => (
                    <div key={comment.id} className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-gray-700 p-4">
                      <div className="flex items-start justify-between mb-3 pb-2 border-b border-gray-700">
                        <button
                          onClick={() => {
                            const profile = {
                              username: comment.username || 'Anonymous',
                              userId: comment.userId,
                              memberSince: comment.timestamp,
                              stats: {
                                streak: comment.streak || 0,
                                sendRate: comment.sendRate || 0,
                                totalSends: 0,
                                longestStreak: 0,
                              },
                              categoryBreakdown: [],
                            };
                            setViewingProfile(profile);
                            setShowProfile(true);
                          }}
                          className="flex items-center gap-2 flex-wrap hover:opacity-80 transition-opacity"
                        >
                          <GeometricAvatar username={comment.username || 'Anonymous'} size={20} />
                          <span className="text-sm font-black text-purple-400 hover:text-purple-300">{comment.username}</span>
                        </button>
                        <span className="text-xs text-gray-500 font-bold">
                          {formatTimestamp(comment.timestamp)}
                        </span>
                      </div>
                      <p className="text-white text-sm font-bold mb-3 break-words leading-relaxed">{comment.text}</p>
                      <div className="flex items-center gap-3">
                        {comment.userId !== userId && (
                          <button
                            onClick={() => flagComment(comment.id, post.id)}
                            disabled={comment.flaggedBy.includes(userId)}
                            className="text-xs font-black text-gray-500 hover:text-red-400 transition-all disabled:opacity-50 uppercase px-2 py-1 border border-gray-700 hover:border-red-500"
                          >
                            🚩 FLAG ({comment.flags})
                          </button>
                        )}
                        {(comment.userId === userId || post.userId === userId) && (
                          <button
                            onClick={() => deleteComment(comment.id, post.id)}
                            className="text-xs font-black text-red-400 hover:text-red-300 transition-all uppercase px-2 py-1 border border-red-700 hover:border-red-500 bg-red-900/20"
                          >
                            DELETE
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="bg-gray-900 border-2 border-gray-700 p-6 text-center mb-4">
                <p className="text-gray-500 text-sm font-black uppercase">NO COMMENTS YET • BE FIRST</p>
              </div>
            )}

            {/* Add Comment Box */}
            <div className="bg-black border-3 border-purple-500 p-4">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
                placeholder="DROP YOUR TAKE..."
                maxLength={MAX_COMMENT_LENGTH}
                className="w-full bg-gray-900 border-2 border-purple-600 p-3 text-white placeholder-gray-500 mb-3 min-h-20 font-bold text-sm focus:outline-none focus:border-purple-400 resize-none"
              />
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-black uppercase ${commentText.length > MAX_COMMENT_LENGTH - 20 ? 'text-orange-400' : 'text-gray-500'}`}>
                  {commentText.length}/{MAX_COMMENT_LENGTH}
                </span>
              </div>
              <button
                onClick={() => addComment(post.id)}
                disabled={commentText.trim().length < MIN_COMMENT_LENGTH || commentCooldown > 0 || isPostingComment}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white py-3 font-black text-sm uppercase border-3 border-purple-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:grayscale flex items-center justify-center gap-2"
              >
                {isPostingComment ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    POSTING...
                  </>
                ) : (
                  <>💬 POST COMMENT</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
